import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { isUltraPlan } from "@/components/shared/PaidPlanRoute";
import { getFiscalProfile, updateFiscalProfile } from "@/services/fiscalService";
import type { TenantCurrentResponse } from "@/services/authService";

export interface SettingsFiscalAutomationCardProps {
  tenant: TenantCurrentResponse;
}

/**
 * Toggle for the monthly auto-generation of the IVA boleto de pago. Ultra-only — Free/Pro plans
 * don't have access to the underlying flow, so showing the toggle would be misleading. Renders
 * nothing for non-Ultra tenants and when the fiscal profile hasn't been created yet (toggle
 * needs the profile to persist).
 */
export function SettingsFiscalAutomationCard({
  tenant,
}: SettingsFiscalAutomationCardProps): JSX.Element | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const isUltra = isUltraPlan(tenant.planId);
  const { data: profile, isLoading } = useQuery({
    queryKey: ["fiscal-profile"],
    queryFn: getFiscalProfile,
    enabled: isUltra,
  });

  // Starts FALSE and reads `=== true`, not `!== false`. The column defaults to
  // false since AutoGenerateIvaBoletoDefaultsOff1796800000000, so an absent or
  // unknown value means off — coercing it to on would show a new tenant a
  // toggle claiming an automation that is not running, which is the same
  // undeclared-reads-as-enabled mistake the IVA régimen select made.
  const [enabled, setEnabled] = useState<boolean>(false);
  useEffect(() => {
    if (profile) setEnabled(profile.autoGenerateIvaBoleto === true);
  }, [profile]);

  const mutation = useMutation({
    mutationFn: (next: boolean) => updateFiscalProfile({ autoGenerateIvaBoleto: next }),
    onMutate: (next) => {
      const previous = enabled;
      setEnabled(next);
      return { previous };
    },
    onError: (_err, _next, ctx) => {
      if (ctx) setEnabled(ctx.previous);
      toast.error(t("settings.fiscalAutomation.saveError"));
    },
    onSuccess: async (_data, next) => {
      await queryClient.invalidateQueries({ queryKey: ["fiscal-profile"] });
      toast.success(
        next
          ? t("settings.fiscalAutomation.toastEnabled")
          : t("settings.fiscalAutomation.toastDisabled"),
      );
    },
  });

  if (!isUltra) return null;
  if (!isLoading && !profile) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base text-foreground">
          {t("settings.fiscalAutomation.title")}
        </CardTitle>
        <CardDescription>{t("settings.fiscalAutomation.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3">
          <Checkbox
            id="autoGenerateIvaBoleto"
            checked={enabled}
            disabled={mutation.isPending || isLoading}
            onCheckedChange={(value) => {
              const next = value === true;
              if (next === enabled || mutation.isPending) return;
              mutation.mutate(next);
            }}
          />
          <div className="flex-1 space-y-1">
            <Label htmlFor="autoGenerateIvaBoleto" className="cursor-pointer text-sm font-medium">
              {t("settings.fiscalAutomation.toggleLabel")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t("settings.fiscalAutomation.toggleHint")}
            </p>
          </div>
          {mutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
